import { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Clock, X, ExternalLink } from 'lucide-react';
import UrgencyDot from '@/components/shared/UrgencyDot.jsx';
import { cn } from '@/lib/utils.js';

const PolicyDocsDropSheet = lazy(() => import('@/components/sheets/PolicyDocsDropSheet.jsx'));
const PreStageRenewalSheet = lazy(() => import('@/components/sheets/PreStageRenewalSheet.jsx'));
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { snoozeTask, clearTask } from '@/services/traffic-pattern.service.js';
import { toDate } from '@/lib/date-display.js';

const SELF_DIRECTED = new Set(['initiate_renewal', 'open_doc_drop', 'open_quote_entry']);
const HAS_INLINE_ACTION = new Set(['initiate_renewal', 'open_doc_drop']);

function parseDate(value) {
  if (!value) return null;
  return toDate(value);
}

function StatusLine({ item }) {
  if (!item.due_date && !item.deal_policy_end) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const key = item.prompt_template_key;

  if (key === 'renewal.initiation_due' && item.deal_policy_end) {
    const expDate = parseDate(item.deal_policy_end);
    const expShort = expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const daysOverdue = item.due_date
      ? Math.round((today - parseDate(item.due_date)) / 86400000)
      : 0;
    if (daysOverdue > 0) {
      return <span className="text-accent">Exp {expShort} · {daysOverdue}d overdue</span>;
    }
    // Not overdue: read "Renewal due Aug 1" at a glance (a bare "244d" makes
    // the broker do the math — David, 2026-06-16). Falls back to the expiry
    // when there's no due date.
    const dueShort = item.due_date
      ? parseDate(item.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : null;
    return <span className="text-text-muted">{dueShort ? `Renewal due ${dueShort}` : `Exp ${expShort}`}</span>;
  }

  if (key === 'policy.docs_after_bind') {
    const daysAgo = item.triggered_at
      ? Math.round((today - toDate(item.triggered_at)) / 86400000)
      : 0;
    const dueShort = item.due_date
      ? parseDate(item.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    return <span className="text-text-muted">Bound {daysAgo}d ago{dueShort ? ` · drop by ${dueShort}` : ''}</span>;
  }

  if (key === 'quote.presented_no_decision') {
    const daysAgo = item.triggered_at
      ? Math.round((today - toDate(item.triggered_at)) / 86400000)
      : 0;
    return <span className="text-text-muted">Presented {daysAgo}d ago</span>;
  }

  if (key === 'carrier.submission_stale') {
    const daysAgo = item.triggered_at
      ? Math.round((today - toDate(item.triggered_at)) / 86400000)
      : 0;
    return <span className="text-text-muted">Sent {daysAgo}d, no response</span>;
  }

  if (item.due_date) {
    const dueShort = parseDate(item.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return <span className="text-text-muted">Due {dueShort}</span>;
  }

  return null;
}

function isCounterpartyDirected(item) {
  return ['open_email_template', 'open_compose', 'initiate_compose_for_counterparty'].includes(item.primary_action_type);
}

// MODULE SCOPE is mandatory — defined inside TaskRow's render it would remount
// every render (the V1 tab/keystroke bug). Each instance owns its open state:
// desktop + mobile menus previously shared one menuOpen, so opening either
// mounted BOTH popover portals and the CSS-hidden layout's popover dismissed
// the visible one as an outside interaction in the same tick.
function TaskRowMenu({ item, busy, onSnooze, onCustomSnooze, onDismissOpen, onNavigate, variant }) {
  const [open, setOpen] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const mobile = variant === 'mobile';
  const rowCls = cn(
    'flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-border/40',
    mobile && 'min-h-[44px]'
  );

  async function snooze(days) {
    if (await onSnooze(days)) setOpen(false);
  }

  async function customSnooze() {
    if (await onCustomSnooze(customDate)) setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {mobile ? (
          <button
            type="button"
            aria-label="Task actions"
            data-interactive
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-border text-text-dim hover:bg-border/40"
          >
            <MoreHorizontal size={16} strokeWidth={1.5} />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Task actions"
            onPointerDown={(e) => e.stopPropagation()}
            className="flex h-8 w-8 items-center justify-center rounded-full text-text-dim hover:bg-border/40 hover:text-text opacity-50 hover:opacity-100 group-hover:opacity-100"
          >
            <MoreHorizontal size={16} strokeWidth={1.5} />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} collisionPadding={16} className="w-52 space-y-1 p-2">
        <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">Snooze</p>
        {[
          { label: '+3 days', days: 3 },
          { label: '+7 days', days: 7 },
          { label: '+14 days', days: 14 },
        ].map(({ label, days }) => (
          <button
            key={days}
            type="button"
            disabled={busy}
            onClick={() => snooze(days)}
            className={cn(rowCls, 'text-text')}
          >
            <Clock size={14} strokeWidth={1.5} className="text-text-dim" />
            {label}
          </button>
        ))}
        <div className="flex items-center gap-1 px-2 py-1">
          <input
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            className={cn('flex-1 rounded border border-border bg-bg px-2 py-1 text-xs text-text', mobile && 'min-h-[44px]')}
          />
          <Button variant="outline" size="sm" disabled={busy || !customDate} onClick={customSnooze} className={mobile ? 'min-h-[44px]' : undefined}>
            Set
          </Button>
        </div>
        <div className="border-t border-border pt-1 space-y-0.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => { setOpen(false); onDismissOpen(); }}
            className={cn(rowCls, 'text-accent')}
          >
            <X size={14} strokeWidth={1.5} />
            Dismiss…
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onNavigate(); }}
            className={cn(rowCls, 'text-text')}
          >
            Open account
          </button>
          {item.zoho_task_id && (
            <a
              href={`https://crm.zoho.com/crm/ndemnifly/tab/Tasks/${item.zoho_task_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(rowCls, 'text-text')}
            >
              <ExternalLink size={14} strokeWidth={1.5} className="text-text-dim" />
              View in CRM
            </a>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function TaskRow({ item, onMutate }) {
  const navigate = useNavigate();
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('not_applicable');
  const [dismissNote, setDismissNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [preStageOpen, setPreStageOpen] = useState(false);

  const target = item.account_id
    ? `/account/${encodeURIComponent(item.account_id)}/deal/${encodeURIComponent(item.deal_id)}`
    : `/account/${encodeURIComponent(item.deal_id)}/deal/${encodeURIComponent(item.deal_id)}`;

  // Returns true on success so TaskRowMenu can close itself on resolve
  // (failure keeps the menu open for another try).
  async function handleSnooze(days) {
    if (!item.zoho_task_id) {
      toast.error('Cannot snooze — no Zoho task linked');
      return false;
    }
    setBusy(true);
    try {
      await snoozeTask(item.zoho_task_id, days);
      toast.success(`Snoozed ${days} days`);
      onMutate?.();
      return true;
    } catch (err) {
      toast.error(`Snooze failed: ${err.message || err.code}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleCustomSnooze(dateStr) {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = toDate(dateStr);
    const days = Math.round((targetDate - today) / 86400000);
    if (days < 1) {
      toast.error('Pick a future date');
      return false;
    }
    return handleSnooze(days);
  }

  async function handleDismiss() {
    if (!item.zoho_task_id) {
      toast.error('Cannot dismiss — no Zoho task linked');
      return;
    }
    setBusy(true);
    try {
      await clearTask(item.zoho_task_id, {
        reason: dismissReason,
        note: dismissNote.trim() || undefined,
      });
      toast.success('Task dismissed');
      setDismissOpen(false);
      onMutate?.();
    } catch (err) {
      toast.error(`Dismiss failed: ${err.message || err.code}`);
    } finally {
      setBusy(false);
    }
  }

  function handleRowClick(e) {
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[data-interactive]')) {
      e.stopPropagation();
      return;
    }
    navigate(target);
  }

  const hasManualSubject = !item.primary_action_type;
  const renewalUnready = item.primary_action_type === 'initiate_renewal'
    && item.renewal_readiness?.ready === false;

  // Spine (design-reference/traffic-pattern.preview.html): who / what / reason
  // (the why-now — the heart) / value / action / ⋯. ND-APP number, carrier
  // badges, and the missing-field chips are dropped from the row — readiness is
  // signaled by the action label ('Pre-stage data' when unready).
  const who = hasManualSubject ? item.subject : (item.deal_account_name || '—');
  const fleet = Array.isArray(item.deal_aircraft) ? item.deal_aircraft : [];
  const ac0 = fleet[0] || null;
  const counterparty = isCounterpartyDirected(item) ? item.counterparty_name : null;
  const val = item.deal_premium ? `$${item.deal_premium.toLocaleString()}` : '—';
  const actionLabel = renewalUnready ? 'Pre-stage data' : (item.primary_action_label || 'Open deal');

  function runAction(e) {
    e.stopPropagation();
    if (item.primary_action_type === 'initiate_renewal') {
      setPreStageOpen(true);
    } else if (item.primary_action_type && HAS_INLINE_ACTION.has(item.primary_action_type)) {
      setActionOpen(true);
    } else {
      navigate(target);
    }
  }

  // "what" line: mono tail · make/model (+N for a fleet) · with counterparty.
  const whatNode = (ac0 || counterparty) ? (
    <>
      {ac0 ? (
        <>
          <span className="font-mono text-text-muted">{ac0.tail_number}</span>
          {ac0.make_model ? ` · ${ac0.make_model}` : ''}
          {fleet.length > 1 ? ` +${fleet.length - 1}` : ''}
        </>
      ) : null}
      {counterparty ? <>{ac0 ? ' · ' : ''}with {counterparty}</> : null}
    </>
  ) : null;

  return (
    <>
      {/* Desktop: fixed-column grid so reason/value/action/⋯ align across rows */}
      <div
        onClick={handleRowClick}
        className={cn(
          'group relative cursor-pointer rounded-md border border-border bg-surface transition-colors',
          'hover:border-border-strong active:bg-border/10',
          'hidden md:grid grid-cols-[12px_minmax(0,1fr)_180px_92px_152px_18px] items-center gap-3 px-3.5 py-2.5'
        )}
      >
        <UrgencyDot urgency={item.current_severity} className="shrink-0" />

        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-text">{who}</div>
          {whatNode ? <div className="mt-0.5 truncate text-xs text-text-dim">{whatNode}</div> : null}
        </div>

        {/* reason — the why-now (StatusLine), right-aligned, accent when overdue */}
        <div className="truncate text-right text-xs">
          <StatusLine item={item} />
        </div>

        <div className="truncate text-right font-mono text-[13px] text-text-muted">{val}</div>

        <div data-interactive onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" size="sm" className="h-8 w-full px-2 text-xs" onClick={runAction}>
            {actionLabel}
          </Button>
        </div>

        <div className="flex justify-center" data-interactive onClick={(e) => e.stopPropagation()}>
          <TaskRowMenu
            item={item}
            busy={busy}
            onSnooze={handleSnooze}
            onCustomSnooze={handleCustomSnooze}
            onDismissOpen={() => setDismissOpen(true)}
            onNavigate={() => navigate(target)}
            variant="desktop"
          />
        </div>
      </div>

      {/* Mobile: stacked card (same spine, single column; ≥44px targets) */}
      <div
        onClick={handleRowClick}
        className={cn(
          'group relative cursor-pointer rounded-md border border-border bg-surface p-3 transition-colors',
          'hover:border-border-strong active:bg-border/10',
          'md:hidden'
        )}
      >
        <div className="flex items-start gap-2">
          <UrgencyDot urgency={item.current_severity} className="mt-1.5" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-text">{who}</div>
            {whatNode ? <div className="mt-0.5 truncate text-xs text-text-dim">{whatNode}</div> : null}

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
              {item.deal_premium ? <span className="font-mono text-text-muted">{val}</span> : null}
              <StatusLine item={item} />
            </div>

            <div className="mt-2 flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-11 flex-1 text-xs" data-interactive onClick={runAction}>
                {actionLabel}
              </Button>
              <TaskRowMenu
                item={item}
                busy={busy}
                onSnooze={handleSnooze}
                onCustomSnooze={handleCustomSnooze}
                onDismissOpen={() => setDismissOpen(true)}
                onNavigate={() => navigate(target)}
                variant="mobile"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dismiss Dialog — shared between desktop/mobile */}
      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Dismiss task</DialogTitle>
            <DialogDescription className="text-xs text-text-dim">
              {item.subject}
            </DialogDescription>
          </DialogHeader>
          <fieldset className="space-y-2">
            {[
              { value: 'not_applicable', label: 'Not applicable' },
              { value: 'dismissed', label: 'Already done' },
              { value: 'satisfied', label: 'Other' },
            ].map(({ value, label }) => (
              <label
                key={value}
                className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-text hover:bg-border/20 cursor-pointer min-h-[44px]"
              >
                <input
                  type="radio"
                  name="dismiss_reason"
                  value={value}
                  checked={dismissReason === value}
                  onChange={() => setDismissReason(value)}
                  className="accent-accent"
                />
                {label}
              </label>
            ))}
            <textarea
              value={dismissNote}
              onChange={(e) => setDismissNote(e.target.value)}
              placeholder="Optional note…"
              rows={2}
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </fieldset>
          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setDismissOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleDismiss} disabled={busy}>
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline action sheets */}
      <Suspense fallback={null}>
        {preStageOpen && (
          <PreStageRenewalSheet
            intent="initiate"
            open={preStageOpen}
            onOpenChange={setPreStageOpen}
            dealId={item.deal_id}
            accountId={item.account_id}
            initialAccountName={item.deal_account_name || '—'}
            initialAircraft={item.deal_aircraft || []}
            initialPremium={item.deal_premium}
            onPreStaged={() => {
              setPreStageOpen(false);
              onMutate?.();
            }}
          />
        )}
        {actionOpen && item.primary_action_type === 'open_doc_drop' && (
          <PolicyDocsDropSheet
            open={actionOpen}
            onOpenChange={setActionOpen}
            dealId={item.deal_id}
            taskId={item.zoho_task_id}
            onSuccess={() => { onMutate?.(); setActionOpen(false); }}
          />
        )}
      </Suspense>
    </>
  );
}
